import {
  FileLock2,
  Package,
  Shirt,
  ScrollText,
  ShieldCheck,
  ClipboardCheck,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";
import {
  Nav,
  Footer,
  PageHero,
  CtaBlock,
  TrustStrip,
  usePageSeo,
  SECURE_IMG_URL,
} from "../layout/shared";

const MATERIAL_CLASSES = [
  {
    icon: ScrollText,
    t: "Confidential documents",
    d: "Paper records, files and printed data taken off site and destroyed under an agreed process.",
  },
  {
    icon: Package,
    t: "Branded and rejected stock",
    d: "Obsolete, faulty or branded product that must not reach resale or the open market.",
  },
  {
    icon: Shirt,
    t: "Uniforms and workwear",
    d: "Branded clothing and kit removed from circulation and destroyed under an agreed method.",
  },
  {
    icon: FileLock2,
    t: "Recalled or sensitive products",
    d: "Recalled goods and other sensitive items where a specific destruction method is required.",
  },
];

const OUR_APPROACH = [
  {
    n: "1",
    t: "Understand the material and the risk",
    d: "We agree what the material is, why it must be destroyed and the outcome you need to demonstrate.",
  },
  {
    n: "2",
    t: "Confirm the method and route",
    d: "We propose the destruction method, the security controls, chain-of-custody arrangements and the receiving or partner site, in writing before collection.",
  },
  {
    n: "3",
    t: "Collect and control the movement",
    d: "Collection is arranged through a registered waste carrier with agreed access, vehicle and handling controls.",
  },
  {
    n: "4",
    t: "Destroy under the agreed process",
    d: "Destruction is carried out directly or by a specialist partner depending on the material class. Method, location and evidence are set out in the quotation.",
  },
  {
    n: "5",
    t: "Evidence and recovery",
    d: "You receive the agreed evidence pack for your records. Separated material is recovered where a practical compliance route is available.",
  },
];

const WE_DO_NOT_HANDLE = [
  "Data-bearing electrical equipment or media unless the WEEE, data-destruction and downstream treatment route is confirmed in writing",
  "Food, liquids and animal by-product material at Eardiston unless the site holds current approvals for that activity",
  "Hazardous or unknown chemicals",
  "Mixed composites requiring a specialist route that has not been agreed in writing",
  "Anything without customer authority or legal title to destroy",
];

const Secure = () => {
  usePageSeo({
    title: "Secure Product Destruction · 3 Counties Recycling",
    description:
      "Secure destruction for documents, branded stock and complex products, with agreed chain-of-custody evidence and material recovery where possible.",
    path: "/secure-destruction",
  });

  return (
    <main data-testid="secure-root" className="bg-white text-[#1a1a1a]">
      <Nav />
      <PageHero
        kicker="Secure destruction · confidential · sensitive"
        title="Controlled destruction, with the"
        accent="evidence to prove it."
        body="Controlled destruction for confidential documents, branded stock, recalled or obsolete products and other sensitive materials, with an agreed evidence package and recovery of separated materials where a practical compliance route is available."
        image={SECURE_IMG_URL}
      />

      <section className="bg-white py-16 border-b border-[#024120]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <TrustStrip variant="light" />
        </div>
      </section>

      {/* Material classes */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
              / What we can destroy
            </p>
            <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
              A destruction route matched to the material class.
            </h2>
            <p className="mt-6 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
              The method, security controls and evidence depend on what the
              material is and the risk it carries. We agree those in
              writing before anything moves.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {MATERIAL_CLASSES.map((m) => (
              <div
                key={m.t}
                data-testid={`secure-class-${m.t
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-[#F2F2F2] p-7 border border-[#024120]/5 flex flex-col"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center bg-[#024120] text-[#46B649]">
                  <m.icon className="w-6 h-6" strokeWidth={1.6} />
                </span>
                <p className="mt-6 font-display font-bold text-lg text-[#024120]">
                  {m.t}
                </p>
                <p className="mt-2 text-[15px] text-[#4A4A4A] leading-relaxed">
                  {m.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our approach */}
      <section className="bg-[#024120] text-white py-24 md:py-32 grain overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <div className="mb-14 max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#FF6600]">
              / Our approach
            </p>
            <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-[1.1]">
              Five steps from enquiry to destruction evidence.
            </h2>
            <p className="mt-6 text-white/80 text-base md:text-lg leading-relaxed">
              We describe our documented security controls, chain of custody
              and Article 28 processing terms where relevant. The customer,
              as controller, remains responsible for deciding whether the
              arrangements provide appropriate safeguards.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
            {OUR_APPROACH.map((s) => (
              <div
                key={s.n}
                data-testid={`secure-step-${s.n}`}
                className="bg-white/5 border border-white/10 p-6 flex flex-col"
              >
                <span className="font-display font-black text-4xl text-[#46B649] leading-none">
                  {s.n}
                </span>
                <p className="mt-4 font-display font-bold text-lg tracking-tight">
                  {s.t}
                </p>
                <p className="mt-2 text-[14px] text-white/75 leading-relaxed">
                  {s.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Evidence */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
            / Evidence and reporting
          </p>
          <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
            The paperwork you actually need to keep.
          </h2>
          <p className="mt-6 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
            The specific evidence provided depends on the material class,
            destruction method and downstream outlet. We confirm what the
            job will produce before it starts. That may include a collection
            record, a destruction record for the agreed method, downstream
            transfer information and, where the arrangement supports it, a
            statement of the recovery route for separated material.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              {
                icon: ShieldCheck,
                t: "Documented security controls",
                d: "Access, storage, transport and destruction controls set out in the quotation and contract.",
              },
              {
                icon: ClipboardCheck,
                t: "Chain-of-custody information",
                d: "Movement records covering collection, transport, receipt and destruction.",
              },
              {
                icon: ArrowRight,
                t: "Material recovery where possible",
                d: "Separated material routed to an authorised reprocessor where a practical compliance route is available.",
              },
            ].map((row) => (
              <li
                key={row.t}
                className="flex items-start gap-4 bg-[#F2F2F2] border-l-4 border-[#46B649] px-6 py-5"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center bg-[#024120] text-[#46B649] shrink-0">
                  <row.icon className="w-5 h-5" strokeWidth={1.7} />
                </span>
                <div>
                  <p className="font-display font-bold text-[#024120]">
                    {row.t}
                  </p>
                  <p className="mt-1 text-[15px] text-[#4A4A4A] leading-relaxed">
                    {row.d}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* What we don't handle */}
      <section className="bg-[#F2F2F2] py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="flex items-start gap-4 mb-8">
            <span className="inline-flex h-12 w-12 items-center justify-center bg-[#FF6600] text-white shrink-0">
              <AlertTriangle className="w-6 h-6" strokeWidth={1.7} />
            </span>
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
                / What we do not handle as standard
              </p>
              <h2 className="mt-2 font-display font-black text-2xl md:text-3xl tracking-tight text-[#024120] leading-[1.15]">
                Some material classes require a separately confirmed route.
              </h2>
            </div>
          </div>
          <ul className="space-y-3">
            {WE_DO_NOT_HANDLE.map((m) => (
              <li
                key={m}
                className="bg-white border-l-4 border-[#FF6600] px-5 py-4 text-[15px] text-[#024120] leading-relaxed"
              >
                {m}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-[#4A4A4A] leading-relaxed italic">
            We describe our security controls, chain of custody and evidence
            package accurately for each job. We do not use blanket
            accreditation, guarantee or zero-landfill claims where the
            underlying evidence has not been independently checked.
          </p>
        </div>
      </section>

      <CtaBlock
        title="Talk to us before the material leaves your building."
        body="Send an outline of the material, quantity, sensitivity and the outcome you need to demonstrate. We will describe the method, evidence and route in writing before anything is booked."
        primaryLabel="Request a destruction quote"
      />

      <Footer />
    </main>
  );
};

export default Secure;
